/**
 * Aquaduck ⇄ Device Cow API contract.
 *
 * Device Cow runs in its own durable deployment (`devicecow`, see
 * docs/devicecow_platform.md). Aquaduck deployments call it over the
 * in-cluster Service (`DEVICECOW_URL`, e.g.
 * `http://devicecow.aquaduck.svc.cluster.local:8080`) with
 * `Authorization: Bearer <DEVICECOW_TOKEN>`, and reverse-proxy the
 * browser-facing dev routes (`/api/dev/*`, `/dev/term/*`) to it so the
 * development console stays same-origin. Nothing in this file is exposed to browsers directly.
 *
 * Every route lives under `DEVCOW_API_PREFIX`. `<env>` is an environment name
 * (`app.kubernetes.io/instance`): `aquaduck` for the main deployment, `aquaduck-pr-<N>`
 * for a preview.
 */

import type { DevAgent, DevAgentStatus, DevCreatePreviewResponse, DevEnvironment } from "./dev.js";

export const DEVCOW_API_PREFIX = "/v1";

/** Client path of the development console (Dev Chat + PR preview cards), same origin in every environment. */
export const DEV_CONSOLE_PATH = "/dev";
/** Debugger deep link served by devicecow: focuses one environment's coordinator terminal. */
export const devcowCodexShellPath = (environment: string) => `/debug/?environment=${encodeURIComponent(environment)}&focus=coordinator&view=terminal`;

/** Environment name of the main dev deployment. Previews are `previewEnvironmentName(pr)`. */
export const MAIN_ENVIRONMENT_NAME = "aquaduck";
export const previewEnvironmentName = (pr: number) => `aquaduck-pr-${pr}`;
export function environmentPr(name: string): number | null {
  const m = /^aquaduck-pr-(\d+)$/.exec(name);
  return m ? Number(m[1]) : null;
}

// ------------------------------------------------------------------ routes

/** `GET` → `{ ok: true }` when the process accepts requests; `?live=1` also fails when the host loop heartbeat is stale. */
export const DEVCOW_HEALTH_PATH = `${DEVCOW_API_PREFIX}/healthz`;
/** `GET` → DevcowStatus: every hosted environment, its coordinator, agents and request provenance. */
export const DEVCOW_STATUS_PATH = `${DEVCOW_API_PREFIX}/status`;
/** `GET` → `{ environments: DevEnvironment[] }` (same shape the Dev tab already renders). */
export const DEVCOW_ENVIRONMENTS_PATH = `${DEVCOW_API_PREFIX}/environments`;
/** `POST DevcowCreatePreviewRequest` → DevCreatePreviewResponse. */
export const DEVCOW_PREVIEWS_PATH = `${DEVCOW_API_PREFIX}/previews`;
/** `POST DevcowRequest` → DevcowRequestResponse. Confirmed hand-offs from the Aquaduck agent. */
export const DEVCOW_REQUESTS_PATH = `${DEVCOW_API_PREFIX}/requests`;

const env = (name: string) => `${DEVCOW_API_PREFIX}/environments/${encodeURIComponent(name)}`;
/** `GET` → `{ agents: DevAgent[] }` for that environment. */
export const devcowAgentsPath = (environment: string) => `${env(environment)}/agents`;
/** `DELETE` → `{ ok: true }`. Kills the agent's process. */
export const devcowAgentPath = (environment: string, agentId: string) => `${devcowAgentsPath(environment)}/${encodeURIComponent(agentId)}`;
/** Terminal websocket for an agent (same frame protocol as `DEV_TERM_PATH`, see DevTermControl). */
export const devcowAgentTermPath = (environment: string, agentId: string) => `${devcowAgentPath(environment, agentId)}/term`;

// ------------------------------------------------------------------- types

/** Header Aquaduck sets on proxied browser requests: the human who clicked (user id). */
export const DEVCOW_USER_HEADER = "x-aquaduck-user";

export interface DevcowCreatePreviewRequest {
  title?: string | null;
  requestedBy: string;
}

/** Preview accepted before slow bootstrap and GitHub calls finish. */
export interface DevcowCreatePreviewAccepted {
  accepted: true;
  requestId: string;
  status: "creating";
}

/** A confirmed Aquaduck handoff or an explicit development comment mention; enters top-level Dev Chat intake. */
export type DevcowRequestKind = "handoff";

export interface DevcowRequest {
  kind: DevcowRequestKind;
  /** Environment the request came from (`app.kubernetes.io/instance`). */
  environment: string;
  /** Workspace id inside that environment (`DEFAULT_WORKSPACE_ID` for the default one). */
  workspaceId: string;
  /** Human who asked (user id). */
  requestedBy: string;
  /** Chat message id or comment id that carries the request, when it exists in the document. */
  messageId?: string;
  commentId?: string;
  /** Focused one-up object, when the request came from a focused chat. */
  focusObjectId?: string;
  /** The request text as the model should read it. */
  text: string;
  /** `aquaduck` after the user's yes; `user` for a direct development comment mention. */
  source?: "aquaduck" | "user";
}

export interface DevcowRequestResponse {
  accepted: boolean;
  /** Request id Device Cow tracks the work under. */
  requestId: string;
  /** Short acknowledgement text for the caller to relay (e.g. into the originating chat). */
  message: string;
  /** Where the conversation continues (the development console of the main environment, deep-linked to the request thread). */
  devTabUrl: string;
}

export type CoordinatorTier = "devicecow" | "preview-coordinator";

export interface DevcowCoordinatorStatus {
  tier: CoordinatorTier;
  /** User id used in that environment's document (`devcow` or `devcow-pr-<N>`). */
  userId: string;
  /** Awareness status text, if any. */
  status: string | null;
  /** Epoch ms of the last completed model turn. */
  lastTurnAt?: number | null;
  connected: boolean;
}

/** Aggregated CI status of the preview's PR, as last seen by Device Cow. */
export interface DevcowPrChecks {
  state: "pending" | "success" | "failure" | "unknown";
  total?: number;
  passed?: number;
  failed?: number;
  /** Link to the checks tab / failing run, when known. */
  url?: string;
  /** Last material change to checks; coordinator lastReportAt tracks poll freshness. */
  updatedAt?: number;
}

/** Applied only to future previews created by this user. */
export interface DevcowPreferences { mergeWhenReadyByDefault: boolean }

export type DevPreviewStage = "creating-issue" | "deploying-preview" | "launching-coordinator" | "implementing" | "ready-for-review" | "reviewing" | "merged" | "blocked";
/** Durable wall-clock observations; absent history must never be inferred from the current stage. */
export interface DevPrTiming {
  /** GitHub's PR created_at, distinct from the human request's createdAt. */
  openedAt?: number;
  transitions: { stage: DevPreviewStage; at: number }[];
}
export interface DevPreviewIntake {
  /** Actual issue/preview creation accepted this request; absent on old speculative chat placeholders. */
  accepted?: boolean;
  requestId: string;
  title: string;
  requestedBy: string;
  startedAt: number;
  stage?: DevPreviewStage;
  issue?: number;
  issueUrl?: string;
  blockedReason?: string;
  /** Bounded, credential-redacted creation diagnostic; absent on older failures. */
  errorDetails?: string;
}

export interface DevcowHostStatus {
  hosting?: "hosted" | "connecting" | "unavailable" | "not delegated";
  stage?: DevPreviewStage;
  blockedReason?: string;
  mergedAt?: number;
  timing?: DevPrTiming;
  mergeWhenReady?: boolean;
  /** The current Device Cow host supports the opt-in control for this preview. */
  canMergeWhenReady?: boolean;
  /** Absent until the status producer populates it; render as "checks: unknown". */
  checks?: DevcowPrChecks;
  // ---- request provenance (2026-09-15 redesign; producer: devicecow main host from `previewRequests`)
  /** GitHub issue tracking the request that opened this preview. */
  issue?: number;
  issueUrl?: string;
  /** Feature title (= issue title = PR title); the preview's display name. */
  prTitle?: string;
  /** Human who asked (user id) and their verbatim request. */
  requestedBy?: string;
  requestedByName?: string;
  githubAuthor?: string;
  request?: string;
  /** Root message id of the request thread in main's Dev Chat (`/dev#msg=<id>`). */
  threadMessageId?: string;
  /** Epoch ms: original request creation (historical imports use PR creation). */
  createdAt?: number;
  /** Epoch ms: the PR head's last commit. */
  lastCommitAt?: number;
  /** Debugger deep link for this preview's coordinator terminal (iframe target). */
  codexShellUrl?: string;
  environment: DevEnvironment;
  coordinator: DevcowCoordinatorStatus | null;
  agents: DevAgent[];
}

export interface DevTokenDay { day: string; input: number; output: number }
/** Recorded OpenAI usage from development Codex sessions; input includes cached tokens. */
export interface DevTokenUsage {
  rows: (DevTokenDay & { environment: string; pr?: number; userId?: string; userName?: string })[];
  updatedAt?: number;
}
export interface DevcowStatus {
  usage?: DevTokenUsage;
  leader?: { podName: string; since: number | null; standby: boolean };
  creating?: DevPreviewIntake[];
  /** Deployed devicecow source sha. */
  sha: string | null;
  startedAt: number;
  hosts: DevcowHostStatus[];
}

/** Terminal-status helper shared by the Dev tab and the status endpoint. */
export const isTerminalAgentStatus = (status: DevAgentStatus) => status === "exited" || status === "killed" || status === "lost";
